import { useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

/**
 * Fire-and-forget anonymous page-view tracker for PlayFirst Lite pages.
 *
 * Privacy: the backend hashes the IP with a daily-rotating salt and stores
 * only path + ref domain + UA hash (30-day TTL). No cookies, no PII.
 *
 * Behavior:
 *   - Logs once per `path` per browser session (sessionStorage de-dupe)
 *   - Never throws; failures are silent
 *   - Skipped on non-tracked paths (server also rejects)
 *
 * Usage:
 *   useLitePageView();           // tracks the current location.pathname
 *   useLitePageView('/us/tools/session-bridge');  // explicit override
 */
export default function useLitePageView(explicitPath) {
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current) return;
    const path = explicitPath || (typeof window !== 'undefined' ? window.location.pathname : '');
    if (!path) return;

    // Only track our own routes
    const tracked = path === '/' || path.startsWith('/us') || path.startsWith('/lite');
    if (!tracked) return;

    // De-dupe per session per path
    let sessionKey;
    try {
      sessionKey = `pf_pv_${path}`;
      if (sessionStorage.getItem(sessionKey) === '1') return;
    } catch {/* ignore */}

    const referrer = (typeof document !== 'undefined' && document.referrer) || '';
    sentRef.current = true;
    axios
      .post(`${API}/lite/pageview`, { path, referrer })
      .then(() => {
        try { if (sessionKey) sessionStorage.setItem(sessionKey, '1'); } catch {/* ignore */}
      })
      .catch(() => { /* silent */ });
  }, [explicitPath]);
}
